import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Loader2,
  Send,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { submitProject } from "../lib/api";
import {
  SECTOR_OPTIONS,
  STATUS_OPTIONS,
  CHALLENGE_OPTIONS,
  BUDGET_OPTIONS,
  TIMELINE_OPTIONS,
} from "../lib/brand";

const STEPS = [
  { id: 1, label: "Contact", desc: "Who are we speaking with?" },
  { id: 2, label: "Project", desc: "Sector & current status" },
  { id: 3, label: "Scope", desc: "Vision & challenges" },
  { id: 4, label: "Budget", desc: "Timeline & capital" },
];

const empty = {
  full_name: "",
  email: "",
  company: "",
  phone: "",
  role: "",
  sector: "",
  status: "",
  project_name: "",
  description: "",
  challenges: [],
  stakeholders: "",
  budget_range: "",
  timeline: "",
  location: "",
  additional_notes: "",
};

export const ProjectForm = () => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleChallenge = (c) =>
    setForm((f) => ({
      ...f,
      challenges: f.challenges.includes(c)
        ? f.challenges.filter((x) => x !== c)
        : [...f.challenges, c],
    }));

  const stepValid = () => {
    if (step === 1)
      return form.full_name && form.email && form.company;
    if (step === 2) return form.sector && form.status;
    if (step === 3) return form.project_name && form.description;
    if (step === 4) return form.budget_range && form.timeline;
    return true;
  };

  const next = () => {
    if (!stepValid()) {
      toast.error("Please complete the required fields to continue.");
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length));
  };
  const back = () => setStep((s) => Math.max(s - 1, 1));

  const onSubmit = async () => {
    if (!stepValid()) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await submitProject(form);
      setDone(true);
      toast.success("Project intake received — our team will be in touch.");
    } catch (err) {
      toast.error("Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="project-form-success"
        className="rounded-3xl border border-slate-200 bg-white p-10 text-center shadow-xl shadow-[#00A3E0]/5"
      >
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#00A3E0]/10 text-[#00A3E0]">
          <CheckCircle2 className="h-8 w-8" />
        </div>
        <h3 className="mt-5 font-display text-3xl font-extrabold text-slate-900">
          Thank you — your project is in our queue.
        </h3>
        <p className="mx-auto mt-3 max-w-md text-sm text-slate-600">
          A member of the CPS Consulting team will review your intake and reach
          out within one business day to schedule a discovery call.
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Button
            onClick={() => {
              setDone(false);
              setForm(empty);
              setStep(1);
            }}
            variant="outline"
            data-testid="project-form-submit-another"
            className="rounded-full"
          >
            Submit another project
          </Button>
          <Button
            onClick={() => navigate("/")}
            data-testid="project-form-return-home"
            className="rounded-full bg-[#00A3E0] text-white hover:bg-[#0089bd]"
          >
            Return home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-xl shadow-[#00A3E0]/5 sm:p-10">
      {/* Step indicator */}
      <div className="mb-8 flex items-center gap-3 overflow-x-auto pb-2">
        {STEPS.map((s, i) => {
          const isActive = step === s.id;
          const isDone = step > s.id;
          return (
            <React.Fragment key={s.id}>
              <button
                type="button"
                onClick={() => isDone && setStep(s.id)}
                data-testid={`form-step-indicator-${s.id}`}
                className="flex items-center gap-3 whitespace-nowrap"
              >
                <span
                  className={`flex h-9 w-9 flex-none items-center justify-center rounded-full border text-sm font-bold transition-all ${
                    isActive
                      ? "border-[#00A3E0] bg-[#00A3E0] text-white"
                      : isDone
                      ? "border-[#00A3E0] bg-white text-[#00A3E0]"
                      : "border-slate-200 bg-slate-50 text-slate-400"
                  }`}
                >
                  {isDone ? <CheckCircle2 className="h-5 w-5" /> : s.id}
                </span>
                <div className="hidden sm:block">
                  <p
                    className={`text-xs font-semibold uppercase tracking-[0.18em] ${
                      isActive || isDone ? "text-slate-900" : "text-slate-400"
                    }`}
                  >
                    {s.label}
                  </p>
                  <p className="text-[11px] text-slate-500">{s.desc}</p>
                </div>
              </button>
              {i < STEPS.length - 1 && (
                <span
                  className={`hidden h-px flex-1 sm:block ${
                    step > s.id ? "bg-[#00A3E0]" : "bg-slate-200"
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Progress bar (mobile) */}
      <div className="mb-6 h-1 w-full overflow-hidden rounded-full bg-slate-100 sm:hidden">
        <motion.div
          animate={{ width: `${(step / STEPS.length) * 100}%` }}
          transition={{ duration: 0.4 }}
          className="h-full bg-[#00A3E0]"
        />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.35 }}
        >
          {step === 1 && (
            <div className="space-y-5">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <Field label="Full name *">
                  <Input
                    value={form.full_name}
                    onChange={(e) => update("full_name", e.target.value)}
                    data-testid="project-input-name"
                    placeholder="Jane Doe"
                  />
                </Field>
                <Field label="Email *">
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    data-testid="project-input-email"
                    placeholder="jane@company.com"
                  />
                </Field>
                <Field label="Company *">
                  <Input
                    value={form.company}
                    onChange={(e) => update("company", e.target.value)}
                    data-testid="project-input-company"
                    placeholder="Company / Organization"
                  />
                </Field>
                <Field label="Role / Title">
                  <Input
                    value={form.role}
                    onChange={(e) => update("role", e.target.value)}
                    data-testid="project-input-role"
                    placeholder="e.g. Director of Development"
                  />
                </Field>
                <Field label="Phone">
                  <Input
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    data-testid="project-input-phone"
                    placeholder="+1 555 000 0000"
                  />
                </Field>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <Field label="Project sector *">
                <Select
                  value={form.sector}
                  onValueChange={(v) => update("sector", v)}
                >
                  <SelectTrigger data-testid="project-input-sector">
                    <SelectValue placeholder="Select a sector" />
                  </SelectTrigger>
                  <SelectContent>
                    {SECTOR_OPTIONS.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Current project status *">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {STATUS_OPTIONS.map((s) => {
                    const active = form.status === s;
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => update("status", s)}
                        data-testid={`project-status-${s}`}
                        className={`flex items-center gap-3 rounded-xl border p-4 text-left text-sm font-medium transition-all ${
                          active
                            ? "border-[#00A3E0] bg-[#00A3E0]/[0.06] text-slate-900"
                            : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                        }`}
                      >
                        <span
                          className={`flex h-5 w-5 flex-none items-center justify-center rounded-full border-2 ${
                            active
                              ? "border-[#00A3E0] bg-[#00A3E0]"
                              : "border-slate-300"
                          }`}
                        >
                          {active && (
                            <span className="h-2 w-2 rounded-full bg-white" />
                          )}
                        </span>
                        {s}
                      </button>
                    );
                  })}
                </div>
              </Field>
              <Field label="Location (optional)">
                <Input
                  value={form.location}
                  onChange={(e) => update("location", e.target.value)}
                  data-testid="project-input-location"
                  placeholder="City, State / Country"
                />
              </Field>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <Field label="Project name *">
                <Input
                  value={form.project_name}
                  onChange={(e) => update("project_name", e.target.value)}
                  data-testid="project-input-projectname"
                  placeholder="e.g. Riverbend Logistics Park"
                />
              </Field>
              <Field label="Describe the project *">
                <Textarea
                  value={form.description}
                  onChange={(e) => update("description", e.target.value)}
                  data-testid="project-input-description"
                  placeholder="Vision, scope, objectives, current state, what success looks like…"
                  className="min-h-[120px]"
                />
              </Field>
              <Field label="Key challenges (select all that apply)">
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {CHALLENGE_OPTIONS.map((c) => {
                    const active = form.challenges.includes(c);
                    return (
                      <label
                        key={c}
                        className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 text-sm transition-all ${
                          active
                            ? "border-[#00A3E0] bg-[#00A3E0]/[0.06]"
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        <Checkbox
                          checked={active}
                          onCheckedChange={() => toggleChallenge(c)}
                          data-testid={`project-challenge-${c}`}
                        />
                        <span className="font-medium text-slate-700">{c}</span>
                      </label>
                    );
                  })}
                </div>
              </Field>
              <Field label="Key stakeholders involved">
                <Input
                  value={form.stakeholders}
                  onChange={(e) => update("stakeholders", e.target.value)}
                  data-testid="project-input-stakeholders"
                  placeholder="Investors, JV partners, public bodies, EPCs…"
                />
              </Field>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5">
              <Field label="Estimated budget *">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {BUDGET_OPTIONS.map((b) => {
                    const active = form.budget_range === b;
                    return (
                      <button
                        key={b}
                        type="button"
                        onClick={() => update("budget_range", b)}
                        data-testid={`project-budget-${b}`}
                        className={`rounded-xl border p-3 text-sm font-semibold transition-all ${
                          active
                            ? "border-[#00A3E0] bg-[#0F172A] text-white"
                            : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                        }`}
                      >
                        {b}
                      </button>
                    );
                  })}
                </div>
              </Field>
              <Field label="Timeline to engage *">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
                  {TIMELINE_OPTIONS.map((t) => {
                    const active = form.timeline === t;
                    return (
                      <button
                        key={t}
                        type="button"
                        onClick={() => update("timeline", t)}
                        data-testid={`project-timeline-${t}`}
                        className={`rounded-xl border p-3 text-sm font-semibold transition-all ${
                          active
                            ? "border-[#00A3E0] bg-[#0F172A] text-white"
                            : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                        }`}
                      >
                        {t}
                      </button>
                    );
                  })}
                </div>
              </Field>
              <Field label="Anything else we should know?">
                <Textarea
                  value={form.additional_notes}
                  onChange={(e) => update("additional_notes", e.target.value)}
                  data-testid="project-input-notes"
                  placeholder="Constraints, prior consultants, materials available, links to docs…"
                  className="min-h-[100px]"
                />
              </Field>

              {/* Review preview */}
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                <div className="mb-3 flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[#00A3E0]" />
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-600">
                    Review
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                  <ReviewItem label="Contact" value={`${form.full_name} • ${form.company}`} />
                  <ReviewItem label="Email" value={form.email} />
                  <ReviewItem label="Sector" value={form.sector} />
                  <ReviewItem label="Status" value={form.status} />
                  <ReviewItem label="Project" value={form.project_name} />
                  <ReviewItem
                    label="Challenges"
                    value={form.challenges.join(", ") || "—"}
                  />
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Footer nav */}
      <div className="mt-8 flex flex-col-reverse items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Button
          type="button"
          variant="outline"
          onClick={back}
          disabled={step === 1}
          data-testid="project-form-back-btn"
          className="rounded-full"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
        <div className="flex items-center gap-3">
          <span className="hidden text-xs font-semibold text-slate-500 sm:inline">
            Step {step} of {STEPS.length}
          </span>
          {step < STEPS.length ? (
            <Button
              type="button"
              onClick={next}
              data-testid="project-form-next-btn"
              className="rounded-full bg-[#00A3E0] text-white hover:bg-[#0089bd]"
            >
              Continue <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          ) : (
            <Button
              type="button"
              onClick={onSubmit}
              disabled={submitting}
              data-testid="project-form-submit-btn"
              className="rounded-full bg-[#00A3E0] text-white hover:bg-[#0089bd]"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting…
                </>
              ) : (
                <>
                  Submit project <Send className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      <p className="mt-6 text-center text-[11px] text-slate-500 sm:text-left">
        By submitting, you agree we may reach out about your project.{" "}
        <Link to="/" className="font-semibold text-[#00A3E0] hover:underline">
          Return home
        </Link>
        .
      </p>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-600">
      {label}
    </Label>
    <div className="mt-2">{children}</div>
  </div>
);

const ReviewItem = ({ label, value }) => (
  <div>
    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
      {label}
    </p>
    <p className="mt-0.5 truncate font-medium text-slate-900">{value || "—"}</p>
  </div>
);

export default ProjectForm;
